import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { Router } from '@angular/router';

export type SortButtonVariant = 'primary' | 'secondary' | 'ghost' | 'warn' | 'danger';
export type SortButtonSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'sortaify-button',
  standalone: true,
  imports: [NgClass],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      [type]="type"
      [disabled]="disabled || loading"
      [ngClass]="buttonClasses"
      (click)="onClick($event)">
      @if (loading) {
        <span class="sortaify-btn__spinner" aria-label="Loading"></span>
      }
      @if (!loading) {
        @if (showArrows) {
          <span class="sortaify-btn__arrow sortaify-btn__arrow-left" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              @if (arrowDirection === 'left') {
                <path d="M12.0799 4.22168L3.88816 11.9999L12.0799 19.778L13.4941 18.3638L7.83803 12.9999H20.0082V10.9999H7.83803L13.4941 5.63589L12.0799 4.22168Z"/>
              } @else {
                <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"/>
              }
            </svg>
          </span>
        }
        <span class="sortaify-btn__label">
          <ng-content></ng-content>
        </span>
        @if (showArrows) {
          <span class="sortaify-btn__arrow sortaify-btn__arrow-right" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              @if (arrowDirection === 'left') {
                <path d="M12.0799 4.22168L3.88816 11.9999L12.0799 19.778L13.4941 18.3638L7.83803 12.9999H20.0082V10.9999H7.83803L13.4941 5.63589L12.0799 4.22168Z"/>
              } @else {
                <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"/>
              }
            </svg>
          </span>
        }
      }
    </button>
  `,
  styles: [`
    :host {
      display: inline-flex;
      position: relative;
    }

    .sortaify-btn {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: none;
      cursor: pointer;
      font-family: var(--sortaify-font, inherit);
      font-weight: 600;
      text-decoration: none;
      line-height: 1.4;
      background: transparent;
      overflow: hidden;
      transition:
        transform 150ms ease,
        box-shadow 150ms ease,
        color 150ms ease,
        border-radius 600ms cubic-bezier(0.23, 1, 0.32, 1);
      padding-top: 16px;
      padding-bottom: 16px;
    }

    .sortaify-btn-link {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      position: relative;
      cursor: pointer;
      font-family: var(--sortaify-font, inherit);
      font-weight: 600;
      text-decoration: none;
      line-height: 1.4;
      background: transparent;
      transition:
        transform 150ms ease,
        box-shadow 150ms ease,
        color 150ms ease,
        border-radius 600ms cubic-bezier(0.23, 1, 0.32, 1);
      padding: 8px 20px;
      font-size: 13px;
    }

    /* ---- Size-specific arrow positions ---- */
    .sortaify-btn--sm { padding: 8px 48px 8px 20px; font-size: 13px; }
    .sortaify-btn--sm .sortaify-btn__arrow-right { right: 14px; }
    .sortaify-btn--sm:hover .sortaify-btn__arrow-right { right: -25%; }
    .sortaify-btn--sm:hover .sortaify-btn__arrow-left { left: 14px; }

    .sortaify-btn--md { padding: 12px 56px 12px 28px; font-size: 14px; }
    .sortaify-btn--md .sortaify-btn__arrow-right { right: 16px; }
    .sortaify-btn--md:hover .sortaify-btn__arrow-right { right: -25%; }
    .sortaify-btn--md:hover .sortaify-btn__arrow-left { left: 16px; }

    .sortaify-btn--lg { padding: 16px 64px 16px 32px; font-size: 16px; }
    .sortaify-btn--lg .sortaify-btn__arrow-right { right: 18px; }
    .sortaify-btn--lg:hover .sortaify-btn__arrow-right { right: -25%; }
    .sortaify-btn--lg:hover .sortaify-btn__arrow-left { left: 18px; }

    .sortaify-btn--block { width: 100%; }
    .sortaify-btn:disabled { opacity: 0.5; cursor: not-allowed; pointer-events: none; }
    .sortaify-btn--disabled { opacity: 0.5; cursor: not-allowed; pointer-events: none; }
    .sortaify-btn:focus-visible { outline: 2px solid rgba(0,0,0,0.4); outline-offset: 2px; }

    .sortaify-btn-link {
      padding: 8px 20px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      position: relative;
      cursor: pointer;
      font-family: var(--sortaify-font, inherit);
      font-weight: 600;
      text-decoration: none;
      line-height: 1.4;
      background: transparent;
      border: none;
      font-size: 13px;
    }

    /* ---- Circle expand on hover ---- */
    .sortaify-btn::before {
      content: '';
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%) scale(0);
      width: 100%; height: 100%;
      border-radius: var(--sortaify-radius-pill, 100px);
      opacity: 0;
      transition: transform 600ms cubic-bezier(0.23, 1, 0.32, 1), opacity 600ms cubic-bezier(0.23, 1, 0.32, 1);
      pointer-events: none;
      z-index: 0;
    }
    .sortaify-btn:hover::before { transform: translate(-50%, -50%) scale(1); opacity: 0.15; }

    /* ---- Label ---- */
    .sortaify-btn__label {
      position: relative;
      z-index: 1;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      white-space: nowrap;
      transition: transform 800ms cubic-bezier(0.23, 1, 0.32, 1);
    }

    .sortaify-btn--show-arrows .sortaify-btn__label {
      padding-left: 28px;
    }
    .sortaify-btn:hover .sortaify-btn__label { transform: translateX(6px); }

    /* ---- Arrows ---- */
    .sortaify-btn__arrow {
      position: absolute;
      top: 50%;
      z-index: 1;
      display: inline-flex;
      align-items: center;
      transition:
        left 800ms cubic-bezier(0.23, 1, 0.32, 1),
        right 800ms cubic-bezier(0.23, 1, 0.32, 1),
        color 150ms ease,
        opacity 800ms cubic-bezier(0.23, 1, 0.32, 1);
    }

    .sortaify-btn__arrow-right {
      right: 16px;
      transform: translateY(-50%);
    }

    .sortaify-btn__arrow-left {
      left: -25%;
      transform: translateY(-50%);
      opacity: 0;
    }

    .sortaify-btn:hover .sortaify-btn__arrow-right {
      right: -25%;
      opacity: 0;
    }
    .sortaify-btn:hover .sortaify-btn__arrow-left {
      left: 16px;
      opacity: 1;
    }

    /* ---- Left arrow direction (back button style) ---- */
    .sortaify-btn--arrow-left-dir .sortaify-btn__arrow-right {
      right: -25%;
      opacity: 0;
    }
    .sortaify-btn--arrow-left-dir .sortaify-btn__arrow-left {
      left: 16px;
      opacity: 1;
    }
    .sortaify-btn--arrow-left-dir:hover .sortaify-btn__arrow-right {
      right: 16px;
      opacity: 1;
    }
    .sortaify-btn--arrow-left-dir:hover .sortaify-btn__arrow-left {
      left: -25%;
      opacity: 0;
    }
    .sortaify-btn--show-arrows.sortaify-btn--arrow-left-dir .sortaify-btn__label {
      padding-left: 28px;
      padding-right: 0;
    }
    .sortaify-btn--show-arrows.sortaify-btn--arrow-left-dir .sortaify-btn__label {
      padding-left: 28px;
      padding-right: 0;
    }
    .sortaify-btn--show-arrows.sortaify-btn--arrow-left-dir:hover .sortaify-btn__label {
      transform: translateX(-6px);
    }

    /* ---- Active ---- */
    .sortaify-btn:active { transform: scale(0.95); }

    /* ---- Spinner ---- */
    .sortaify-btn__spinner {
      width: 18px;
      height: 18px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: #ffffff;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
      flex-shrink: 0;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    /* ==================== PRIMARY ==================== */
    .sortaify-btn-primary {
      background: #2395ef !important;
      color: #ffffff !important;
      box-shadow: 0 2px 12px rgba(35, 149, 239, 0.3);
      border-radius: var(--sortaify-radius-pill, 100px) !important;
    }
    .sortaify-btn-primary .sortaify-btn__arrow { color: rgba(255,255,255,0.8); }
    .sortaify-btn-primary::before { background: #ffffff; }

    .sortaify-btn-primary:hover:not(:disabled) {
      background: #ffffff !important;
      color: #000000 !important;
      box-shadow: 0 4px 20px rgba(0,0,0,0.15);
      border-radius: 12px !important;
    }
    .sortaify-btn-primary:hover .sortaify-btn__arrow { color: #000000; }

    /* ==================== SECONDARY ==================== */
    .sortaify-btn-secondary {
      background: rgba(20, 20, 20, 0.9) !important;
      color: #2395ef !important;
      border: 1px solid rgba(255, 255, 255, 0.12) !important;
      border-radius: var(--sortaify-radius-pill, 100px) !important;
      backdrop-filter: blur(12px);
    }
    .sortaify-btn-secondary .sortaify-btn__arrow { color: #2395ef; }
    .sortaify-btn-secondary::before { background: #ffffff; }

    .sortaify-btn-secondary:hover:not(:disabled) {
      background: #ffffff !important;
      color: #000000 !important;
      border-color: rgba(0, 0, 0, 0.15) !important;
      border-radius: 12px !important;
      box-shadow: 0 4px 16px rgba(0,0,0,0.15);
    }
    .sortaify-btn-secondary:hover .sortaify-btn__arrow { color: #000000; }

    /* ==================== GHOST ==================== */
    .sortaify-btn-ghost {
      background: transparent;
      color: var(--sortaify-text-secondary, rgba(255,255,255,0.7));
      border: 1px solid transparent;
      border-radius: var(--sortaify-radius-md, 10px);
      font-size: 14px;
    }
    .sortaify-btn-ghost::before { display: none; }
    .sortaify-btn-ghost:hover:not(:disabled) {
      color: var(--sortaify-text-primary, #ffffff);
      background: var(--sortaify-bg-hover, rgba(30,30,30,0.8));
    }
    .sortaify-btn-ghost .sortaify-btn__arrow { display: none; }

    /* ==================== WARN ==================== */
    .sortaify-btn-warn {
      background: rgba(245, 158, 11, 0.9) !important;
      color: #000000 !important;
      box-shadow: 0 2px 12px rgba(245, 158, 11, 0.3);
      border-radius: var(--sortaify-radius-pill, 100px) !important;
    }
    .sortaify-btn-warn .sortaify-btn__arrow { color: rgba(0,0,0,0.7); }
    .sortaify-btn-warn::before { background: #ffffff; }

    .sortaify-btn-warn:hover:not(:disabled) {
      background: #fef3c7 !important;
      color: #000000 !important;
      box-shadow: 0 4px 20px rgba(245, 158, 11, 0.4);
      border-radius: 12px !important;
    }
    .sortaify-btn-warn:hover .sortaify-btn__arrow { color: #000000; }

    /* ==================== DANGER ==================== */
    .sortaify-btn-danger {
      background: #b91c1c !important;
      color: #ffffff !important;
      box-shadow: 0 2px 12px rgba(185, 28, 28, 0.4);
      border-radius: var(--sortaify-radius-pill, 100px) !important;
    }
    .sortaify-btn-danger .sortaify-btn__arrow { color: rgba(255,255,255,0.8); }
    .sortaify-btn-danger::before { background: #ffffff; }

    .sortaify-btn-danger:hover:not(:disabled) {
      background: #dc2626 !important;
      color: #ffffff !important;
      box-shadow: 0 4px 20px rgba(185, 28, 28, 0.5);
      border-radius: 12px !important;
    }
    .sortaify-btn-danger:hover .sortaify-btn__arrow { color: #ffffff; }

    /* ==================== ICON BUTTON ==================== */
    .sortaify-btn-icon {
      padding: 12px !important;
      border-radius: var(--sortaify-radius-pill, 100px) !important;
      width: 44px; height: 44px; flex-shrink: 0;
      background: #2395ef !important;
      color: #ffffff !important;
    }
    .sortaify-btn-icon .sortaify-btn__arrow { display: none; }
    .sortaify-btn-icon .mat-icon {
      position: relative; z-index: 1;
      font-size: 20px; width: 20px; height: 20px; line-height: 20px;
      color: #ffffff;
    }
    .sortaify-btn-icon.sortaify-btn--sm { padding: 8px !important; width: 32px; height: 32px; }
    .sortaify-btn-icon.sortaify-btn--lg { padding: 16px !important; width: 56px; height: 56px; }
    .sortaify-btn-icon.sortaify-btn--sm .mat-icon { font-size: 16px; width: 16px; height: 16px; line-height: 16px; }
    .sortaify-btn-icon.sortaify-btn--lg .mat-icon { font-size: 24px; width: 24px; height: 24px; line-height: 24px; }

    .sortaify-btn-icon:hover:not(:disabled) {
      background: #ffffff !important;
      color: #000000 !important;
      border-radius: 12px !important;
      box-shadow: 0 4px 16px rgba(0,0,0,0.15);
    }
    .sortaify-btn-icon:hover .mat-icon { color: #000000; }
  `]
})
export class SortButtonComponent {
  private router = inject(Router);

  @Input() variant: SortButtonVariant = 'primary';
  @Input() size: SortButtonSize = 'md';
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() disabled = false;
  @Input() loading = false;
  @Input() block = false;
  @Input() showArrows = true;
  @Input() arrowDirection: 'right' | 'left' = 'right';
  @Input() routerLink: string | any = '';

  @Output() buttonClick = new EventEmitter<MouseEvent>();

  get buttonClasses(): string {
    const classes = ['sortaify-btn', `sortaify-btn-${this.variant}`, `sortaify-btn--${this.size}`];
    if (this.block) classes.push('sortaify-btn--block');
    if (this.showArrows) classes.push('sortaify-btn--show-arrows');
    if (this.arrowDirection === 'left') classes.push('sortaify-btn--arrow-left-dir');
    return classes.join(' ');
  }

  onClick(event: MouseEvent): void {
    if (this.loading) return;
    if (this.routerLink) {
      this.router.navigate(this.routerLink instanceof Array ? this.routerLink : [this.routerLink]);
    }

    // If it's a submit button, let the browser handle form submission natively.
    if (this.type === 'submit') {
      return;
    }

    this.buttonClick.emit(event);
  }
}
